import React from "react";
import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
} from "@mui/material";
import { colors } from "./colors";

const ICON_SIZES = {
  small: 16,
  medium: 20,
  large: 24,
} as const;

export type IconButtonSize = keyof typeof ICON_SIZES | number;

export interface IconButtonProps extends Omit<MuiIconButtonProps, "size"> {
  size?: IconButtonSize;
  ariaLabel: string;
}

const IconButton: React.FC<IconButtonProps> = ({
  size = "large",
  ariaLabel,
  children,
  sx,
  ...props
}) => {
  const pixelSize = typeof size === "number" ? size : ICON_SIZES[size];

  // Clone children to pass size prop to icon components
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(
        child as React.ReactElement<{ size?: number }>,
        {
          size: pixelSize,
        },
      );
    }
    return child;
  });

  return (
    <MuiIconButton
      {...props}
      aria-label={ariaLabel}
      sx={{
        color: colors.neutral.white,
        width: `${pixelSize}px`,
        height: `${pixelSize}px`,
        padding: 0,
        "&:hover": {
          backgroundColor: colors.button.iconButtonHoverBg,
        },
        ...sx,
      }}
    >
      {enhancedChildren}
    </MuiIconButton>
  );
};

export default IconButton;
